"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { GradText, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "./kit";
import { useEnterStagger } from "./kit/motion";
import { routePath } from "@/lib/routes";

const IC = "h-5 w-5";

interface BentoItem {
  span: string;
  icon: ReactNode;
  title: string;
  body: ReactNode;
  /** Feature page this tile links to (omit for tiles with no dedicated page). */
  href?: string;
}

const ITEMS: BentoItem[] = [
  {
    span: "lg:col-span-3",
    icon: (
      <svg className={IC} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M3 3v18h18" />
        <path d="M7 14l4-4 3 3 5-6" />
      </svg>
    ),
    title: "Live analytics",
    body: "Clicks, devices, browsers, referrers and countries — live in the dashboard, bots filtered out, UTM tags broken down.",
    href: routePath("analytics"),
  },
  {
    span: "lg:col-span-3",
    icon: (
      <svg className={IC} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18" />
      </svg>
    ),
    title: "Custom branded domains",
    body: (
      <>
        Run links on{" "}
        <span className="font-jetbrains text-brand-600">go.yourbrand.com</span>.
        Your own domains, automatic SSL, zero DNS headaches.
      </>
    ),
    href: routePath("customDomains"),
  },
  {
    span: "lg:col-span-2",
    icon: (
      <svg className={IC} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M14 14h3v3M21 21v.01" />
      </svg>
    ),
    title: "Branded QR codes",
    body: "Dynamic QR with your logo, colors and shapes — fully trackable, editable after print.",
  },
  {
    span: "lg:col-span-2",
    icon: (
      <svg className={IC} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
      </svg>
    ),
    title: "REST API & event stream",
    body: "Create, route and track links programmatically. Live click events over SSE for links created through the API.",
    href: routePath("developers"),
  },
  {
    span: "lg:col-span-2",
    icon: (
      <svg className={IC} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
      </svg>
    ),
    title: "A/B & rotation",
    body: "Split traffic across destinations by weight, compare results, and shift traffic to the winner.",
    href: routePath("smartRouting"),
  },
];

const PRIVACY_CHIPS = [
  "GDPR / DSGVO",
  "IP anonymisation",
  "Bot filtering",
  "No third-party pixels",
  "99.99% SLA · Enterprise",
  "SSO / SAML on request",
];

function BentoCard({
  span,
  href,
  children,
}: {
  span: string;
  href?: string;
  children: ReactNode;
}) {
  const cls = `group relative col-span-2 block overflow-hidden rounded-ref-lg border border-line bg-surface p-[26px] shadow-ref transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-[3px] hover:border-brand-600/30 hover:shadow-ref-lg ${span}`;
  if (href) {
    return (
      <a data-bento-tile href={href} className={cls}>
        <ArrowUpRight
          size={18}
          className="absolute right-5 top-5 text-line-3 transition-colors group-hover:text-brand-600"
          aria-hidden
        />
        {children}
      </a>
    );
  }
  return <div data-bento-tile className={cls}>{children}</div>;
}

function IconTile({ children }: { children: ReactNode }) {
  return (
    <div className="mb-[18px] grid h-[42px] w-[42px] place-items-center rounded-[11px] border border-brand-600/30 bg-brand-600/[0.08] text-brand-600">
      {children}
    </div>
  );
}

/** "Core principles" bento — the feature grid. */
export default function FeaturesBentoSection() {
  const gridRef = useRef<HTMLDivElement>(null);

  // Only used on the homepage's grid-mode reel — no non-grid consumer, so the
  // plain pair (no null-ref gate) is enough. Default breathing row: the block
  // is a grid of many separate bento cards, not one enclosing opaque card.
  useWholeRowBlocks(gridRef);
  useBandRowAlign(gridRef);

  // Tiles cascade in as a wave across the bento grid.
  useEnterStagger(gridRef, "[data-bento-tile]", {
    y: 28,
    duration: 0.75,
    stagger: { each: 0.08, from: "start" },
  });

  return (
    <SectionShell id="ref-features" divider>
      <SectionHead
        center
        eyebrow="Core principles"
        title={
          <>
            Everything your domain can do, <GradText>in one platform.</GradText>
          </>
        }
      />

      <div ref={gridRef} data-grid-block className="grid grid-cols-2 gap-[18px] lg:grid-cols-6">
          {ITEMS.map((item) => (
            <BentoCard key={item.title} span={item.span} href={item.href}>
              <IconTile>{item.icon}</IconTile>
              <h3 className="mb-2 text-lg font-semibold tracking-[-0.01em]">
                {item.title}
              </h3>
              <p className="text-[14.5px] leading-[1.55] text-muted">{item.body}</p>
            </BentoCard>
          ))}

          {/* full-width privacy row */}
          <BentoCard span="lg:col-span-6" href={routePath("security")}>
            <div className="flex flex-wrap items-center gap-[30px]">
              <div className="min-w-[240px] flex-1">
                <IconTile>
                  <svg className={IC} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </IconTile>
                <h3 className="mb-2 text-lg font-semibold tracking-[-0.01em]">
                  Privacy &amp; compliance, built in
                </h3>
                <p className="max-w-[52ch] text-[14.5px] leading-[1.55] text-muted">
                  Privacy by design — visitor IPs anonymised before storage, bots
                  filtered, no third-party pixels — handled for you; you just get
                  clean data.
                </p>
              </div>
              <div className="flex max-w-[340px] flex-wrap gap-2.5">
                {PRIVACY_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-line bg-surface-2 px-[13px] py-[7px] text-[12.5px] text-muted"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </BentoCard>
      </div>
    </SectionShell>
  );
}
